import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ShieldCheck, Sparkles, Star, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LineReveal, Reveal } from "@/components/site/motion";

export default function Hero({ onGetQuote, onCalculate }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const reduced = useReducedMotion();
  const imgY = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [1, 1.08]);
  const blob1Y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 90]);
  const blob2Y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [0, 40]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden"
    >
      {/* decorative background */}
      <div className="absolute inset-0 -z-10">
        <motion.div
          style={{ y: blob1Y }}
          className="absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full bg-orange-100/70 blur-3xl"
        />
        <motion.div
          style={{ y: blob2Y }}
          className="absolute top-1/3 -left-24 h-[420px] w-[420px] rounded-full bg-orange-50 blur-3xl"
        />
        <div className="absolute inset-0 grain opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full bg-white hairline px-3 py-1.5 text-xs font-bold tracking-wide text-ink-2"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
            Bangalore&apos;s most trusted repainting crew
            <Sparkles className="h-3.5 w-3.5 text-orange-500" />
          </motion.div>

          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] text-ink">
            <LineReveal
              lines={[
                "Professional",
                'repainting for <span class="text-orange-600">homes</span>',
                '<span class="text-orange-600">&amp; apartments</span>',
              ]}
              delay={0.1}
              stagger={0.08}
            />
          </h1>

          <Reveal delay={0.55} className="mt-5 text-base md:text-lg text-ink-2 max-w-xl">
            Affordable, fast &amp; high-quality repainting with branded paints, skilled
            crews and fully transparent pricing. Free site visit across Bangalore.
          </Reveal>

          <Reveal delay={0.7} className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onGetQuote}
              data-testid="hero-cta-quote"
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 h-12 shadow-brand group"
            >
              Get Free Quote
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <Button
              variant="outline"
              onClick={onCalculate}
              data-testid="hero-cta-calculate"
              className="rounded-full h-12 px-7 border-slate-300 text-ink font-bold hover:bg-white"
            >
              Calculate Cost
            </Button>
          </Reveal>

          {/* trust badges */}
          <Reveal delay={0.85} className="mt-9 grid grid-cols-3 gap-3 max-w-xl">
            {[
              { icon: CheckCircle2, label: "100+ Projects" },
              { icon: ShieldCheck, label: "Verified Painters" },
              { icon: Star, label: "Quality Guarantee" },
            ].map((b, i) => (
              <div
                key={i}
                className="rounded-xl bg-white hairline px-3 py-3 flex items-center gap-2.5 shadow-soft"
                data-testid={`trust-badge-${i}`}
              >
                <div className="h-8 w-8 rounded-lg bg-orange-50 grid place-items-center">
                  <b.icon className="h-4 w-4 text-orange-600" />
                </div>
                <div className="text-[13px] font-bold text-ink leading-tight">
                  {b.label}
                </div>
              </div>
            ))}
          </Reveal>

          {/* rating strip */}
          <Reveal delay={0.95} className="mt-6 flex items-center gap-3">
            <div className="flex -space-x-2">
              {[
                "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop",
                "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop",
                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop",
                "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=80&h=80&fit=crop",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="h-8 w-8 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <span className="text-sm font-bold text-ink">4.9</span>
              <span className="text-sm text-muted-ink">· 480+ reviews</span>
            </div>
          </Reveal>
        </div>

        {/* Right: image with clip-path reveal + parallax */}
        <div className="relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.25, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
            className="relative rounded-3xl overflow-hidden shadow-soft hairline aspect-[4/5] bg-white"
          >
            <motion.img
              src="https://images.pexels.com/photos/5493669/pexels-photo-5493669.jpeg"
              alt="Professional painter at work"
              className="w-full h-full object-cover"
              style={{ y: imgY, scale: imgScale }}
            />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 via-black/20 to-transparent">
              <div className="text-white/90 text-xs font-bold tracking-widest uppercase">
                Site in progress
              </div>
              <div className="text-white font-display font-extrabold text-xl">
                Premium Emulsion · Asian Paints
              </div>
            </div>
          </motion.div>

          {/* floating cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="hidden sm:flex absolute -left-6 top-10 rounded-2xl bg-white hairline shadow-soft p-4 w-56 items-center gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-orange-50 grid place-items-center">
              <Sparkles className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-ink">
                Starts at
              </div>
              <div className="font-display font-extrabold text-ink text-lg">
                ₹20K<span className="text-sm text-muted-ink"> · 1 BHK</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -right-4 -bottom-6 rounded-2xl bg-ink text-white p-4 w-56 shadow-soft"
          >
            <div className="text-[11px] font-bold uppercase tracking-widest text-orange-300">
              On-time delivery
            </div>
            <div className="font-display font-extrabold text-2xl mt-1 text-white">98%</div>
            <div className="text-xs text-white/70 mt-1">
              Across 100+ Bangalore projects
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
